import { X } from "lucide-react";
import { formatCurrency, formatUSD, getUsdRate } from "@vaulzo/core/constants";
import { cryptoPriceUSD } from "@vaulzo/core/domain/crypto";

// Deliberately limited to the majors for now (independent of the price
// cache, so the dropdown never renders empty).
const KNOWN_SYMBOLS = ["BTC", "ETH"];

const underline =
  "border-0 border-b-[1.5px] border-vaulzo-input-line bg-transparent py-2 text-vaulzo-ink outline-none transition-colors placeholder:text-vaulzo-subtle focus:border-vaulzo-accent";

/**
 * Row-based editor for a crypto portfolio: one row per coin (dropdown +
 * units + optional buy price), a running total, and per-row live values.
 * The server does the authoritative pricing on save — previews here just
 * mirror the cached prices.
 */
export default function CryptoHoldingsEditor({ holdings, setHoldings, cryptoPrices, exchangeRates }) {
  const patchRow = (i, patch) =>
    setHoldings(holdings.map((h, idx) => (idx === i ? { ...h, ...patch } : h)));
  const removeRow = (i) => setHoldings(holdings.filter((_, idx) => idx !== i));
  const addRow = () => setHoldings([...holdings, { symbol: "BTC", units: "", buyPrice: "" }]);

  // Running totals across rows that have a cached price.
  let totalUSD = 0;
  let invested = 0;
  let investedNow = 0;
  holdings.forEach((h) => {
    const units = parseFloat(h.units);
    const price = cryptoPriceUSD(cryptoPrices, h.symbol);
    if (!(units > 0) || !price) return;
    totalUSD += units * price;
    const buy = parseFloat(h.buyPrice);
    if (buy > 0) {
      invested += units * buy;
      investedNow += units * price;
    }
  });
  const gain = invested > 0 ? ((investedNow - invested) / invested) * 100 : null;

  return (
    <div className="block">
      <div className="mb-3 flex items-baseline justify-between">
        <span className="text-[13px] font-semibold text-vaulzo-muted">Coins</span>
        <span className="text-[12px] text-vaulzo-fainter">Buy price is optional — shows your gain</span>
      </div>

      {/* Column headers */}
      <div className="flex gap-3 pb-1 text-[11px] uppercase tracking-[0.06em] text-vaulzo-fainter">
        <span className="w-[104px]">Coin</span>
        <span className="flex-1">Units</span>
        <span className="w-[110px]">Buy price ($)</span>
        <span className="w-5" />
      </div>

      {holdings.map((h, i) => {
        const units = parseFloat(h.units);
        const price = cryptoPriceUSD(cryptoPrices, h.symbol);
        const buy = parseFloat(h.buyPrice);
        const rowUSD = units > 0 && price ? units * price : null;
        const rowGain = rowUSD && buy > 0 ? ((price - buy) / buy) * 100 : null;

        return (
          <div key={i} className="border-b border-vaulzo-row py-2 last:border-b-0">
            <div className="flex items-center gap-3">
              <span className="w-[104px]">
                <select
                  value={h.symbol}
                  onChange={(e) => patchRow(i, { symbol: e.target.value })}
                  className={`w-full cursor-pointer text-[15px] font-semibold ${underline}`}
                >
                  {/* Keep an existing non-major symbol selectable so old
                      portfolios (e.g. SOL) still render and save correctly */}
                  {(KNOWN_SYMBOLS.includes(h.symbol) ? KNOWN_SYMBOLS : [h.symbol, ...KNOWN_SYMBOLS]).map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </span>
              <input
                type="number"
                step="any"
                min="0"
                inputMode="decimal"
                required
                value={h.units}
                onChange={(e) => patchRow(i, { units: e.target.value })}
                placeholder="0.10034925"
                className={`num flex-1 text-[15px] font-semibold ${underline}`}
              />
              <input
                type="number"
                step="any"
                min="0"
                inputMode="decimal"
                value={h.buyPrice}
                onChange={(e) => patchRow(i, { buyPrice: e.target.value })}
                placeholder="88209.39"
                className={`num w-[110px] text-[15px] ${underline}`}
              />
              <button
                type="button"
                onClick={() => removeRow(i)}
                disabled={holdings.length === 1}
                aria-label="Remove coin"
                className="flex w-5 justify-center text-vaulzo-bar transition-colors hover:text-vaulzo-danger disabled:opacity-30"
              >
                <X className="h-4 w-4" strokeWidth={1.7} />
              </button>
            </div>

            {rowUSD && (
              <div className="num pt-1 text-[12px] text-vaulzo-fainter">
                ≈ {formatUSD(rowUSD, { decimals: 2, suffix: "" })} at{" "}
                {formatUSD(price, { decimals: 2, suffix: "" })}
                {rowGain != null && (
                  <span className={rowGain >= 0 ? "text-vaulzo-accent" : "text-vaulzo-danger"}>
                    {" "}
                    · {rowGain >= 0 ? "↑" : "↓"} {Math.abs(rowGain).toFixed(2)}%
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}

      <button
        type="button"
        onClick={addRow}
        className="mt-3 text-[13px] font-semibold text-vaulzo-accent transition-colors hover:text-vaulzo-accent-hover"
      >
        + Add coin
      </button>

      {/* Portfolio total, like the sheet's summary block */}
      {totalUSD > 0 && (
        <div className="num mt-4 rounded-xl bg-vaulzo-row/60 px-4 py-3 text-[13px] text-vaulzo-muted">
          Total{" "}
          <span className="font-semibold text-vaulzo-ink">
            {formatUSD(totalUSD, { decimals: 2, suffix: "" })}
          </span>{" "}
          ≈ {formatCurrency(totalUSD * getUsdRate(exchangeRates))}
          {gain != null && (
            <span className={`font-semibold ${gain >= 0 ? "text-vaulzo-accent" : "text-vaulzo-danger"}`}>
              {" "}
              · {gain >= 0 ? "↑" : "↓"} {Math.abs(gain).toFixed(2)}%
            </span>
          )}
        </div>
      )}
    </div>
  );
}
